import { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Grid,
    IconButton,
    MenuItem,
    Tooltip,
    Typography
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { PrimaryTextField } from 'theme/CommonTheme';
import {
    normalizeApiIdentifierEntry,
    normalizeSelectorsForPut
} from 'components/TrustCenter/store/utils/agreementV2Normalization';
import * as Constants from 'constants/Constants';
import SelectorIdentifierChipList, {
    SelectorJoinControl
} from './SelectorIdentifierChipList';
import SelectorIdentifierLineChip from './SelectorIdentifierLineChip';
import PresetKeyAutocomplete from './PresetKeyAutocomplete';
import { validateQuickAddKey } from './quickAddKeyValidation';
import {
    buildIdentifierBracketSelector,
    CARRIER_IDENTIFIERS_FIELD,
    CUSTOMER_REFERENCE_FIELD,
    identifierFieldMenuLabel,
    selectorFieldOptionsForEvent,
    SHIPPER_IDENTIFIERS_FIELD
} from './selectorBuilderUtils';
import {
    appendSelectorClause,
    headerSummariesFromSelectorPredicate,
    parseSelectorChain,
    removeVisibleSelectorClause,
    replaceVisibleSelectorClauseTexts,
    selectorsForStore,
    setVisibleSelectorJoin,
    visibleSelectorClauses
} from './selectorDisplayUtils';
import css from './SharingAgreement.module.css';

/** Stable id per sharing-rule row; kept in memory only (stripped on PUT by normalizeMessageTypesForPut). */
function newClientRowId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `row-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Ensures each messageTypes group has `_clientRowId` so list order survives
 * rows ↔ messageTypes round-trips and React can key accordions without index bugs.
 * @param {Record<string, unknown[]>|undefined} messageTypes
 */
export function patchMessageTypesRowIds(messageTypes) {
    if (!messageTypes || typeof messageTypes !== 'object' || Array.isArray(messageTypes)) {
        return messageTypes;
    }
    let changed = false;
    const out = {};
    for (const [eventKey, groups] of Object.entries(messageTypes)) {
        if (!Array.isArray(groups)) {
            out[eventKey] = groups;
            continue;
        }
        const newGroups = groups.map((g) => {
            if (g && typeof g === 'object' && g._clientRowId) {
                return g;
            }
            changed = true;
            const base = g && typeof g === 'object' ? { ...g } : {};
            return { ...base, _clientRowId: newClientRowId() };
        });
        out[eventKey] = newGroups;
    }
    return changed ? out : messageTypes;
}

function serializeField(value) {
    if (value == null) return '';
    if (Array.isArray(value)) return JSON.stringify(value);
    return String(value);
}

/** One array element shown in a text field (strings plain; objects/numbers as JSON). */
function itemToInputString(item) {
    if (item == null) return '';
    if (typeof item === 'string') return item;
    const keys = item.keys ?? item.Keys;
    const path = item.path ?? item.Path;
    if (path != null && Array.isArray(keys) && keys.length > 0) {
        const p = String(path).trim();
        const ks = keys.map(String);
        if (ks.length === 1) return `path=${p}; key=${ks[0]}`;
        return `path=${p}; keys=${ks.join(', ')}`;
    }
    return JSON.stringify(item);
}

function apiValueToItemStrings(value) {
    if (value == null) return [];
    if (Array.isArray(value)) {
        return value.length ? value.map(itemToInputString) : [];
    }
    return [serializeField(value)].filter(Boolean);
}

/**
 * Maps one identifiers textarea line into stored `messageTypes[].identifiers` entry.
 * Keeps partial lines when normalization fails.
 */
function parseIdentifierLineForStore(text) {
    const raw = String(text);
    if (raw === '') return '';
    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch {
        parsed = raw;
    }
    const n = normalizeApiIdentifierEntry(parsed);
    return n != null ? n : parsed;
}

/**
 * Preset reference number keys offered as suggestions in the quick-add Autocomplete.
 * `ReferenceNumbersList` uses `[Key='…'].Value`; `ReferenceNumbers` is a plain object
 * map (no Value field) — use `ReferenceNumbers['…']` instead. Custom keys typed by
 * the user are accepted after passing {@link validateQuickAddKey}.
 */
const REFERENCE_NUMBER_LIST_KEYS = ['BL#', 'BOL', 'SID'];

/** V2 selector PATH dropdown: id + default JSONata-style path when custom path is empty. */
const SELECTOR_PATH_OPTIONS = [
    { id: 'ReferenceNumbersList', pathExpr: '$.ReferenceNumbersList' },
    { id: 'ReferenceNumbers', pathExpr: '$.ReferenceNumbers' }
];

/**
 * @param {string} pathOptionId
 * @returns {string}
 */
function resolveSelectorPathExpr(pathOptionId) {
    const id = String(pathOptionId ?? '').trim();
    const def = SELECTOR_PATH_OPTIONS.find((o) => o.id === id);
    return def ? def.pathExpr : '';
}

/**
 * @param {string} pathExpr
 * @param {string[]} keys
 * @returns {string}
 */
function buildSelectorPathKeysLine(pathExpr, keys) {
    const p = String(pathExpr ?? '').trim();
    if (keys.length === 1) return `path=${p}; key=${keys[0]}`;
    return `path=${p}; keys=${keys.join(', ')}`;
}

/**
 * @param {{ disabled: boolean, onAppend: (line: string) => void }} props
 */
function SelectorPathKeysQuickAdd({ disabled, onAppend }) {
    const [pathOptionId, setPathOptionId] = useState(SELECTOR_PATH_OPTIONS[0]?.id ?? '');
    const [primaryKey, setPrimaryKey] = useState('');
    const [quickAddError, setQuickAddError] = useState('');

    const handleAdd = () => {
        setQuickAddError('');
        const pathExpr = resolveSelectorPathExpr(pathOptionId);
        if (!pathExpr) {
            setQuickAddError('Choose a path.');
            return;
        }
        const validation = validateQuickAddKey(primaryKey);
        if (!validation.ok) {
            setQuickAddError(validation.message);
            return;
        }
        onAppend(buildSelectorPathKeysLine(pathExpr, [validation.value]));
        setPrimaryKey('');
    };

    return (
        <Box className={css.quickAddSelectorSection}>
            <Box className={css.quickAddSelectorFields}>
                <Box className={css.quickAddSelectorRow}>
                    <PrimaryTextField
                        className={css.quickAddSelectorFieldTopLevelSelect}
                        label="PATH"
                        value={pathOptionId}
                        onChange={(e) => setPathOptionId(e.target.value)}
                        disabled={disabled}
                        fullWidth={false}
                        select
                        size="small"
                        SelectProps={{ displayEmpty: true }}
                    >
                        {SELECTOR_PATH_OPTIONS.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                                {opt.id}
                            </MenuItem>
                        ))}
                    </PrimaryTextField>
                    <PresetKeyAutocomplete
                        className={css.quickAddSelectorFieldKeyAutocomplete}
                        label="Primary Key"
                        options={REFERENCE_NUMBER_LIST_KEYS}
                        value={primaryKey}
                        onChange={setPrimaryKey}
                        disabled={disabled}
                        placeholder="Select or type a key"
                    />
                    <Button
                        className={css.quickAddSelectorAddButton}
                        variant="outlined"
                        color="primary"
                        size="small"
                        disabled={disabled}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                </Box>
                {quickAddError ? (
                    <Typography variant="caption" color="error" display="block">
                        {quickAddError}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}

/**
 * @param {{ disabled: boolean, onAppend: (expr: string, operator: 'and'|'or'|null) => void, eventTypeKey?: string, showCombineWith?: boolean }} props
 */
function QuickAddSelectorBar({ disabled, onAppend, eventTypeKey = '', showCombineWith = false }) {
    const [selectorField, setSelectorField] = useState('ReferenceNumbersList');
    const [selectorKey, setSelectorKey] = useState('');
    const [selectorValue, setSelectorValue] = useState('');
    const [quickAddError, setQuickAddError] = useState('');
    const [combineWith, setCombineWith] = useState('and');
    const fieldOptions = selectorFieldOptionsForEvent(eventTypeKey);

    const keyNotApplicable =
        selectorField === SHIPPER_IDENTIFIERS_FIELD ||
        selectorField === CUSTOMER_REFERENCE_FIELD;
    const carrierOrgTypeKey = selectorField === CARRIER_IDENTIFIERS_FIELD;
    const customKeyApplicable = !keyNotApplicable && !carrierOrgTypeKey;

    const handleAdd = () => {
        setQuickAddError('');
        let normalizedKey = selectorKey;
        if (customKeyApplicable) {
            const validation = validateQuickAddKey(selectorKey);
            if (!validation.ok) {
                setQuickAddError(validation.message);
                return;
            }
            normalizedKey = validation.value;
        }
        const { expr, error } = buildIdentifierBracketSelector(
            selectorField,
            normalizedKey,
            '=',
            selectorValue,
            eventTypeKey
        );
        if (error) {
            setQuickAddError(error);
            return;
        }
        onAppend(expr, showCombineWith ? combineWith : null);
        setSelectorKey('');
        setSelectorValue('');
    };

    let selectorKeyField;
    if (carrierOrgTypeKey) {
        selectorKeyField = (
            <PrimaryTextField
                className={css.quickAddSelectorFieldKey}
                label="Organization identifier type"
                value={selectorKey}
                onChange={(e) => setSelectorKey(e.target.value)}
                disabled={disabled}
                fullWidth={false}
                size="small"
                placeholder="e.g. 1, 5, 6"
            />
        );
    } else {
        selectorKeyField = (
            <PresetKeyAutocomplete
                className={css.quickAddSelectorFieldKeyAutocomplete}
                label="Key"
                options={REFERENCE_NUMBER_LIST_KEYS}
                value={selectorKey}
                onChange={setSelectorKey}
                disabled={disabled || keyNotApplicable}
                placeholder="Select or type a key"
            />
        );
    }

    return (
        <Box className={css.quickAddSelectorSection}>
            <Box className={css.quickAddSelectorFields}>
                <Box className={css.quickAddSelectorRow}>
                    <PrimaryTextField
                        className={css.quickAddSelectorFieldTopLevelSelect}
                        label="Field"
                        value={selectorField}
                        onChange={(e) => {
                            const v = e.target.value;
                            const leavingCarrier =
                                selectorField === CARRIER_IDENTIFIERS_FIELD &&
                                v !== CARRIER_IDENTIFIERS_FIELD;
                            const enteringCarrierOrShipper =
                                v === SHIPPER_IDENTIFIERS_FIELD || v === CARRIER_IDENTIFIERS_FIELD;
                            const enteringCustomerReference = v === CUSTOMER_REFERENCE_FIELD;
                            if (leavingCarrier || enteringCarrierOrShipper || enteringCustomerReference) {
                                setSelectorKey('');
                            }
                            setSelectorField(v);
                        }}
                        disabled={disabled}
                        fullWidth={false}
                        select
                        size="small"
                    >
                        {fieldOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {identifierFieldMenuLabel(opt)}
                            </MenuItem>
                        ))}
                    </PrimaryTextField>
                    {keyNotApplicable ? (
                        <Tooltip title="key is not applicable for the selected field">
                            <span className={css.quickAddSelectorKeyFieldWrap}>
                                {selectorKeyField}
                            </span>
                        </Tooltip>
                    ) : (
                        selectorKeyField
                    )}
                    <Typography
                        className={css.quickAddSelectorOpEquals}
                        component="span"
                        aria-hidden="true"
                    >
                        =
                    </Typography>
                    <PrimaryTextField
                        className={css.quickAddSelectorFieldValue}
                        label="Value"
                        value={selectorValue}
                        onChange={(e) => setSelectorValue(e.target.value)}
                        disabled={disabled}
                        size="small"
                        placeholder='Text, number, or JSON e.g. ["a","b"]'
                    />
                    <Button
                        className={css.quickAddSelectorAddButton}
                        variant="outlined"
                        color="primary"
                        size="small"
                        disabled={disabled}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                </Box>
                {showCombineWith ? (
                    <Box className={css.quickAddCombineRow}>
                        <Typography variant="caption" color="textSecondary" component="span">
                            Combine with previous
                        </Typography>
                        <SelectorJoinControl value={combineWith} onChange={setCombineWith} />
                    </Box>
                ) : null}
                {quickAddError ? (
                    <Typography variant="caption" color="error" display="block">
                        {quickAddError}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}

function readGroupSelectors(group) {
    const g = group || {};
    return g.selectors ?? g.Selectors;
}

function readGroupIdentifiers(group) {
    const g = group || {};
    return g.identifiers ?? g.Identifiers;
}

/**
 * @param {Record<string, Array<{ groupName?: string, selectors?: unknown, identifiers?: unknown, respondedStatus?: string }>>|undefined} messageTypes
 */
export function flattenMessageTypes(messageTypes) {
    if (!messageTypes || typeof messageTypes !== 'object') return [];
    const rows = [];
    Object.keys(messageTypes).forEach((dictionaryKey) => {
        const groups = messageTypes[dictionaryKey] || [];
        groups.forEach((g) => {
            const storedSelectors = normalizeSelectorsForPut(readGroupSelectors(g));
            rows.push({
                rowId: g._clientRowId,
                dictionaryKey,
                groupName: g.groupName ?? '',
                // selectorsItems ↔ API identifiers (path/key lines).
                // selectorClauses ↔ API selectors, with the join before each predicate.
                selectorsItems: apiValueToItemStrings(readGroupIdentifiers(g)),
                storedSelectors,
                selectorClauses: parseSelectorChain(storedSelectors),
                respondedStatus: g.respondedStatus
            });
        });
    });
    return rows;
}

/**
 * Row with no event, name, or any selector/identifier line — e.g. an unused "Add group" accordion.
 * These must not block Save when another row is fully filled (see `sharingRuleGroupsAreComplete`).
 */
export function isSharingRuleGroupPlaceholderRow(row) {
    const dk = String(row?.dictionaryKey ?? '').trim();
    const gn = String(row?.groupName ?? '').trim();
    const selectorLines = Array.isArray(row?.selectorClauses)
        ? row.selectorClauses.map((clause) => clause?.text)
        : row?.identifiersItems || [];
    const selLines = row?.selectorsItems || [];
    const hasAnyLine = [...selectorLines, ...selLines].some((s) => String(s ?? '').trim() !== '');
    return !dk && !gn && !hasAnyLine;
}

/**
 * @param {Array<{ dictionaryKey?: string, groupName?: string, selectorsItems?: string[], identifiersItems?: string[] }>} rows
 */
export function sharingRuleGroupsAreComplete(rows) {
    if (!rows || rows.length === 0) return true;
    return rows.every((r) => {
        if (isSharingRuleGroupPlaceholderRow(r)) return true;
        return (
            String(r.dictionaryKey ?? '').trim() !== '' && String(r.groupName ?? '').trim() !== ''
        );
    });
}

/**
 * True when there is at least one non-placeholder row with event type and group name.
 * Used when creating a new agreement: {@link sharingRuleGroupsAreComplete} is vacuously true for zero rows.
 */
export function sharingRuleGroupsHaveAtLeastOneCompleteRow(rows) {
    if (!rows || rows.length === 0) return false;
    return rows.some(
        (r) =>
            !isSharingRuleGroupPlaceholderRow(r) &&
            String(r.dictionaryKey ?? '').trim() !== '' &&
            String(r.groupName ?? '').trim() !== ''
    );
}

/**
 * @param {Array<{ dictionaryKey: string, groupName: string, selectorsItems: string[], identifiersItems: string[], respondedStatus?: string }>} rows
 */
export function rowsToMessageTypes(rows) {
    const out = {};
    rows.forEach((row) => {
        const key = row.dictionaryKey != null ? String(row.dictionaryKey) : '';
        if (!out[key]) out[key] = [];
        const selectors = Array.isArray(row.selectorClauses)
            ? selectorsForStore(row.selectorClauses, row.storedSelectors)
            : normalizeSelectorsForPut(row.identifiersItems);
        const identifiers = (row.selectorsItems || []).map(parseIdentifierLineForStore);
        const entry = {
            groupName: row.groupName ?? '',
            selectors,
            identifiers
        };
        if (row.respondedStatus) entry.respondedStatus = row.respondedStatus;
        if (row.rowId) entry._clientRowId = row.rowId;
        out[key].push(entry);
    });
    return out;
}

const blurKey = (rowId, field) => `${String(rowId ?? '')}__${field}`;

/**
 * API `dictionaryKey` / messageTypes object keys use full type names.
 * Known track events get short labels; anything else is shown as the full event name.
 */
const EVENT_TYPE_DISPLAY_BY_API = Object.freeze({
    'com.trimble.trans.track.position.event': 'Position',
    'com.trimble.trans.track.location.event': 'Location',
    'com.trimble.trans.track.shipment.event': 'Shipment',
    'com.trimble.trans.track.stopstatus.event': 'Stop Status'
});

const KNOWN_V2_EVENT_TYPE_ORDER = Constants.V2_SHARING_AGREEMENTS_EVENTS.map((e) => e.name);

function eventTypeNameToDisplayLabel(eventName) {
    const s = String(eventName ?? '').trim();
    if (!s) return '';
    return EVENT_TYPE_DISPLAY_BY_API[s] ?? s;
}

/**
 * Sorted display label + API event name for each message type that has at least one group.
 * @param {Record<string, unknown[]>|undefined} messageTypes
 * @returns {{ label: string, apiName: string }[]}
 */
export function listEventTypeDisplayItemsWithGroups(messageTypes) {
    if (!messageTypes || typeof messageTypes !== 'object') {
        return [];
    }
    const items = [];
    for (const [eventName, groups] of Object.entries(messageTypes)) {
        if (!Array.isArray(groups) || groups.length === 0) {
            continue;
        }
        const eventNameStr = String(eventName ?? '').trim();
        const label = eventTypeNameToDisplayLabel(eventName);
        items.push({ label, apiName: eventNameStr });
    }
    items.sort((a, b) => {
        const byLabel = String(a.label).localeCompare(String(b.label), undefined, {
            sensitivity: 'base'
        });
        if (byLabel !== 0) return byLabel;
        return String(a.apiName).localeCompare(String(b.apiName), undefined, {
            sensitivity: 'base'
        });
    });
    return items;
}

/**
 * Sorted unique UI labels for event/message types that have at least one group in `messageTypes`.
 * @param {Record<string, unknown[]>|undefined} messageTypes
 * @returns {string[]}
 */
export function listEventTypeDisplayLabelsWithGroups(messageTypes) {
    const labels = listEventTypeDisplayItemsWithGroups(messageTypes).map((item) => item.label);
    return [...new Set(labels)].sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { sensitivity: 'base' })
    );
}

/** Event type option entries are API names (strings) or `{ name }` objects. */
function capabilityToApiName(entry) {
    if (entry == null || entry === '') return '';
    if (typeof entry === 'string') return entry.trim();
    if (typeof entry === 'object' && entry.name != null) return String(entry.name).trim();
    return '';
}

function parseObjectFromLine(text) {
    try {
        const parsed = JSON.parse(String(text));
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed;
        }
    } catch {
        /* fall through */
    }
    return null;
}

function normalizeKeySummaryValues(value) {
    if (value == null) return [];
    const values = Array.isArray(value) ? value : [value];
    return values.map((v) => String(v).trim()).filter(Boolean);
}

function keySummariesFromIdentifierLine(value) {
    const text = String(value ?? '').trim();
    if (!text) return [];

    const parsed = parseObjectFromLine(text);
    const parsedKeys = parsed?.keys ?? parsed?.Keys ?? parsed?.key ?? parsed?.Key;
    const parsedKeyValues = normalizeKeySummaryValues(parsedKeys);
    if (parsedKeyValues.length > 0) {
        return parsedKeyValues;
    }

    const keys = [];
    const keyMatch = text.match(/(?:^|;)\s*key\s*=\s*([^;]+)/i);
    if (keyMatch) {
        keys.push(keyMatch[1].trim());
    }

    const keysMatch = text.match(/(?:^|;)\s*keys\s*=\s*([^;]+)/i);
    if (keysMatch) {
        keys.push(
            ...keysMatch[1]
                .split(',')
                .map((k) => k.trim())
                .filter(Boolean)
        );
    }

    return keys;
}

function summarizeHeaderChipValues(items, summarizeValue) {
    const summaries = [];
    const seen = new Set();
    for (const item of items || []) {
        const itemSummaries = normalizeKeySummaryValues(summarizeValue(item));
        for (const summary of itemSummaries) {
            const key = summary.toLocaleLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            summaries.push(summary);
        }
    }
    return summaries;
}

function HeaderExpressionChipRow({ label, items, summarizeValue, ariaLabelPrefix }) {
    const values = summarizeHeaderChipValues(items, summarizeValue);

    return (
        <Box className={css.summaryMetaRow}>
            <Typography
                variant="caption"
                color="textSecondary"
                component="span"
                className={css.summaryMetaLabel}
            >
                {label}:
            </Typography>
            {values.length === 0 ? (
                <Typography variant="caption" color="textSecondary" component="span">
                    None
                </Typography>
            ) : (
                <Box
                    className={css.summaryChipList}
                    component="span"
                    role="list"
                    aria-label={`${ariaLabelPrefix} in accordion header`}
                >
                    {values.map((value) => (
                        <Box
                            key={value}
                            className={css.summaryChipItem}
                            component="span"
                            role="listitem"
                        >
                            <SelectorIdentifierLineChip
                                value={value}
                                isReadOnly
                                className={css.summaryChip}
                                ariaLabels={{
                                    edit: `Edit ${ariaLabelPrefix} line`,
                                    delete: `Delete ${ariaLabelPrefix} line`
                                }}
                                lineAriaLabel={`${ariaLabelPrefix}: ${value}`}
                            />
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

const SharingRuleGroupsSection = ({
    messageTypes,
    onMessageTypesChange,
    isReadOnly,
    eventTypeOptions = []
}) => {
    const rows = flattenMessageTypes(messageTypes);
    const rawFromOptions = new Set(
        (Array.isArray(eventTypeOptions) ? eventTypeOptions : [])
            .map(capabilityToApiName)
            .filter(Boolean)
    );
    const knownOrdered = KNOWN_V2_EVENT_TYPE_ORDER.filter((id) => rawFromOptions.has(id));
    const extra = [...rawFromOptions]
        .filter((id) => !KNOWN_V2_EVENT_TYPE_ORDER.includes(id))
        .sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }));
    const capabilityApiNames = [...knownOrdered, ...extra];
    const [blurredRequiredFields, setBlurredRequiredFields] = useState({});
    const [openStoredSelectors, setOpenStoredSelectors] = useState({});
    const [copiedStoredKey, setCopiedStoredKey] = useState('');

    const copyStoredSelector = async (copyKey, text) => {
        const value = String(text ?? '');
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(value);
            } else {
                const area = document.createElement('textarea');
                area.value = value;
                document.body.appendChild(area);
                area.select();
                document.execCommand('copy');
                document.body.removeChild(area);
            }
            setCopiedStoredKey(copyKey);
        } catch {
            setCopiedStoredKey('');
        }
    };

    const applyRows = (nextRows) => {
        onMessageTypesChange(rowsToMessageTypes(nextRows));
    };

    const markRequiredFieldBlurred = (rowIndex, field) => {
        setBlurredRequiredFields((prev) => ({
            ...prev,
            [blurKey(rowIndex, field)]: true
        }));
    };

    const showRequiredFieldError = (rowIndex, field, value) => {
        if (isReadOnly) return false;
        if (!blurredRequiredFields[blurKey(rowIndex, field)]) return false;
        return String(value ?? '').trim() === '';
    };

    const handleRowFieldChange = (index, field, value) => {
        const next = rows.map((r, i) => {
            if (i !== index) return r;
            const patch = { [field]: value };
            if (field === 'dictionaryKey' && !r.rowId) {
                patch.rowId = newClientRowId();
            }
            return { ...r, ...patch };
        });
        applyRows(next);
    };

    const handleAddGroup = () => {
        applyRows([
            ...rows,
            {
                rowId: newClientRowId(),
                dictionaryKey: '',
                groupName: '',
                selectorsItems: [],
                selectorClauses: [],
                storedSelectors: []
            }
        ]);
    };

    const handleRemoveGroup = (index) => {
        setBlurredRequiredFields({});
        applyRows(rows.filter((_, i) => i !== index));
    };

    const setRowItems = (rowIndex, field, nextItems) => {
        const next = rows.map((r, i) => (i === rowIndex ? { ...r, [field]: nextItems } : r));
        applyRows(next);
    };

    const appendLineToItemsField = (rowIndex, field, expr) => {
        let next = [...(rows[rowIndex][field] || [])];
        while (next.length > 0 && String(next[next.length - 1]).trim() === '') {
            next.pop();
        }
        next.push(expr);
        setRowItems(rowIndex, field, next);
    };

    const appendSelectorExpression = (rowIndex, expr) => {
        appendLineToItemsField(rowIndex, 'selectorsItems', expr);
    };

    const setSelectorClauses = (rowIndex, selectorClauses) => {
        const next = rows.map((r, i) => (i === rowIndex ? { ...r, selectorClauses } : r));
        applyRows(next);
    };

    const appendSelectorPredicate = (rowIndex, expr, operator) => {
        const current = rows[rowIndex]?.selectorClauses || [];
        setSelectorClauses(rowIndex, appendSelectorClause(current, expr, operator));
    };

    const summaryLabel = (row, index) => {
        const name = (row.groupName || '').trim();
        const key = (row.dictionaryKey || '').trim();
        const keyLabel = key ? eventTypeNameToDisplayLabel(key) : '';
        if (name && keyLabel) return `${name} — ${keyLabel}`;
        if (name) return name;
        if (keyLabel) return keyLabel;
        return `Group ${index + 1}`;
    };

    return (
        <Box>
            <Box className={css.sharingRuleGroupsHeader}>
                <Typography variant="subtitle1" className={css.detailSectionTitle}>
                    Sharing rule groups
                </Typography>
                {!isReadOnly && (
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={handleAddGroup}
                    >
                        Add group
                    </Button>
                )}
            </Box>

            {rows.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                    No groups yet. {!isReadOnly && 'Use Add group to create one.'}
                </Typography>
            ) : (
                rows.map((row, index) => {
                    const rowKey = row.rowId ?? `idx-${index}`;
                    const selectorsDisplayed = row.selectorsItems;
                    const visibleClauses = visibleSelectorClauses(row.selectorClauses);
                    const selectorsForDisplay = visibleClauses.map((clause) => clause.text);
                    const selectorJoins = visibleClauses.map((clause) => clause.operatorBefore);
                    const storedSelectorTexts = row.storedSelectors || [];
                    const hasVisibleSelectors = (selectorsDisplayed || []).some(
                        (s) => String(s).trim() !== ''
                    );
                    const hasVisibleUserSelectors = selectorsForDisplay.length > 0;
                    const storedSelectorOpen = Boolean(openStoredSelectors[rowKey]);
                    const eventTypeTouchedError = showRequiredFieldError(
                        rowKey,
                        'dictionaryKey',
                        row.dictionaryKey
                    );
                    const groupNameTouchedError = showRequiredFieldError(
                        rowKey,
                        'groupName',
                        row.groupName
                    );

                    return (
                        <Accordion key={rowKey} className={css.sharingRuleGroupAccordion}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box className={css.summaryRoot}>
                                    <Box className={css.summaryContainer}>
                                        <Box className={css.summaryTitleRow}>
                                            <Typography
                                                className={css.summaryTitle}
                                                component="span"
                                            >
                                                {summaryLabel(row, index)}
                                            </Typography>
                                        </Box>
                                        <HeaderExpressionChipRow
                                            label="Selector Keys"
                                            items={selectorsForDisplay}
                                            summarizeValue={headerSummariesFromSelectorPredicate}
                                            ariaLabelPrefix="Selector keys"
                                        />
                                        <HeaderExpressionChipRow
                                            label="Identifier Keys"
                                            items={selectorsDisplayed}
                                            summarizeValue={keySummariesFromIdentifierLine}
                                            ariaLabelPrefix="Identifier keys"
                                        />
                                    </Box>
                                    {!isReadOnly && (
                                        <Box className={css.summaryActionContainer}>
                                            <IconButton
                                                size="small"
                                                aria-label="Delete sharing rule group"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveGroup(index);
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <PrimaryTextField
                                            required
                                            name={`sharingRuleEventType-${rowKey}`}
                                            label="Event Type"
                                            value={row.dictionaryKey}
                                            onChange={(e) =>
                                                handleRowFieldChange(
                                                    index,
                                                    'dictionaryKey',
                                                    e.target.value
                                                )
                                            }
                                            onBlur={() =>
                                                markRequiredFieldBlurred(rowKey, 'dictionaryKey')
                                            }
                                            disabled={isReadOnly}
                                            fullWidth
                                            select
                                            size="small"
                                            SelectProps={{ displayEmpty: true }}
                                            inputProps={{ required: true }}
                                            error={eventTypeTouchedError}
                                            helperText={
                                                eventTypeTouchedError
                                                    ? 'Event type is required'
                                                    : ''
                                            }
                                        >
                                            <MenuItem value="">
                                                <em>Select event type</em>
                                            </MenuItem>
                                            {capabilityApiNames.map((apiName) => (
                                                <MenuItem key={apiName} value={apiName}>
                                                    {eventTypeNameToDisplayLabel(apiName)}
                                                </MenuItem>
                                            ))}
                                            {row.dictionaryKey &&
                                            !capabilityApiNames.includes(
                                                String(row.dictionaryKey)
                                            ) ? (
                                                <MenuItem value={row.dictionaryKey}>
                                                    {eventTypeNameToDisplayLabel(row.dictionaryKey)}
                                                </MenuItem>
                                            ) : null}
                                        </PrimaryTextField>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <PrimaryTextField
                                            required
                                            name={`sharingRuleGroupName-${rowKey}`}
                                            label="Group Name"
                                            value={row.groupName}
                                            onChange={(e) =>
                                                handleRowFieldChange(
                                                    index,
                                                    'groupName',
                                                    e.target.value
                                                )
                                            }
                                            onBlur={() =>
                                                markRequiredFieldBlurred(rowKey, 'groupName')
                                            }
                                            disabled={isReadOnly}
                                            fullWidth
                                            inputProps={{ required: true }}
                                            error={groupNameTouchedError}
                                            helperText={
                                                groupNameTouchedError
                                                    ? 'Group name is required'
                                                    : ''
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="subtitle2"
                                            component="h4"
                                            className={css.detailSubsectionTitle}
                                        >
                                            Selector
                                        </Typography>
                                        {!isReadOnly && (
                                            <QuickAddSelectorBar
                                                key={`quick-bracket-${rowKey}-${row.dictionaryKey}`}
                                                disabled={isReadOnly}
                                                eventTypeKey={row.dictionaryKey}
                                                showCombineWith={
                                                    (row.selectorClauses || []).length > 0
                                                }
                                                onAppend={(expr, operator) =>
                                                    appendSelectorPredicate(index, expr, operator)
                                                }
                                            />
                                        )}
                                        {!hasVisibleUserSelectors && isReadOnly ? (
                                            <Typography variant="body2" color="textSecondary">
                                                None
                                            </Typography>
                                        ) : (
                                            <SelectorIdentifierChipList
                                                items={selectorsForDisplay}
                                                joinOperators={selectorJoins}
                                                wrapItems
                                                onJoinOperatorChange={(visibleIndex, op) =>
                                                    setSelectorClauses(
                                                        index,
                                                        setVisibleSelectorJoin(
                                                            row.selectorClauses,
                                                            visibleIndex,
                                                            op
                                                        )
                                                    )
                                                }
                                                onItemsChange={(next, removedIndex) =>
                                                    setSelectorClauses(
                                                        index,
                                                        Number.isInteger(removedIndex)
                                                            ? removeVisibleSelectorClause(
                                                                  row.selectorClauses,
                                                                  removedIndex
                                                              )
                                                            : replaceVisibleSelectorClauseTexts(
                                                                  row.selectorClauses,
                                                                  next
                                                              )
                                                    )
                                                }
                                                isReadOnly={isReadOnly}
                                                ariaLabelPrefix="Selector keys"
                                            />
                                        )}
                                        {storedSelectorTexts.length > 0 ? (
                                            <Box>
                                                <Button
                                                    type="button"
                                                    color="primary"
                                                    size="small"
                                                    className={css.storedSelectorToggle}
                                                    aria-expanded={storedSelectorOpen}
                                                    onClick={() =>
                                                        setOpenStoredSelectors((prev) => ({
                                                            ...prev,
                                                            [rowKey]: !prev[rowKey]
                                                        }))
                                                    }
                                                >
                                                    {storedSelectorOpen
                                                        ? 'Hide stored selector'
                                                        : 'Show stored selector'}
                                                </Button>
                                                {storedSelectorOpen
                                                    ? storedSelectorTexts.map((text, selectorIndex) => {
                                                          const copyKey = `${rowKey}-${selectorIndex}`;
                                                          return (
                                                              <Box
                                                                  key={copyKey}
                                                                  className={css.storedSelectorBlock}
                                                              >
                                                                  <pre className={css.storedSelectorText}>
                                                                      {text}
                                                                  </pre>
                                                                  <Box className={css.storedSelectorCopyRow}>
                                                                      <Button
                                                                          type="button"
                                                                          size="small"
                                                                          variant="outlined"
                                                                          color="primary"
                                                                          onClick={() =>
                                                                              copyStoredSelector(
                                                                                  copyKey,
                                                                                  text
                                                                              )
                                                                          }
                                                                      >
                                                                          {copiedStoredKey === copyKey
                                                                              ? 'Copied'
                                                                              : 'Copy'}
                                                                      </Button>
                                                                  </Box>
                                                              </Box>
                                                          );
                                                      })
                                                    : null}
                                            </Box>
                                        ) : null}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="subtitle2"
                                            component="h4"
                                            className={css.detailSubsectionTitle}
                                        >
                                            Identifier
                                        </Typography>
                                        {!isReadOnly && (
                                            <SelectorPathKeysQuickAdd
                                                key={`quick-path-${rowKey}`}
                                                disabled={isReadOnly}
                                                onAppend={(line) =>
                                                    appendSelectorExpression(index, line)
                                                }
                                            />
                                        )}

                                        {!hasVisibleSelectors && isReadOnly ? (
                                            <Typography variant="body2" color="textSecondary">
                                                None
                                            </Typography>
                                        ) : (
                                            <SelectorIdentifierChipList
                                                items={selectorsDisplayed || []}
                                                onItemsChange={(next) =>
                                                    setRowItems(index, 'selectorsItems', next)
                                                }
                                                isReadOnly={isReadOnly}
                                                ariaLabelPrefix="Identifier keys"
                                            />
                                        )}
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    );
                })
            )}
        </Box>
    );
};

export default SharingRuleGroupsSection;
